/**
 * @fileoverview Hook for managing migration dataset templates (local-first + liveQuery).
 * @module system-web/hooks/useDatasetTemplates
 *
 * Responsibilities:
 * - Subscribe to the dataset template list from Dexie via liveQuery.
 * - Subscribe to a single selected template via liveQuery.
 * - Provide imperative actions: create, update, softDelete, activate, deactivate, sample.
 *   All mutations POST to system-api and let sync pull the result.
 *
 * Dependencies:
 * - datasetTemplateService (reads + mutations)
 * - dexie/liveQuery
 *
 * Architecture:
 * - liveQuery observed functions are pure Dexie reads (listDatasetTemplates, getDatasetTemplate).
 * - Writes (create, update, activate, deactivate, softDelete, sample) are imperative —
 *   called from UI handlers, NEVER inside liveQuery.
 * - Local optimistic error state only — no optimistic record insertion (server-authoritative).
 *
 * Notes:
 * - statusFilter defaults to 'all'; pass 'draft' or 'active' to narrow results.
 * - selectedId drives the single-template subscription.
 * - liveQuery observed function ONLY reads — NEVER writes to Dexie.
 */

import { useState, useEffect, useCallback } from 'react';
import { liveQuery } from 'dexie';
import {
  listDatasetTemplates,
  getDatasetTemplate,
  createDatasetTemplate,
  updateDatasetTemplate,
  softDeleteDatasetTemplate,
  activateDatasetTemplate,
  deactivateDatasetTemplate,
  sampleDatasetTemplate,
  type CreateDatasetTemplateInput,
  type UpdateDatasetTemplateInput,
  type SampleDatasetTemplateInput,
} from '../services/datasetTemplateService';
import type { MigrationDatasetTemplate } from '../types/legacyMigrationTypes';

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

/**
 * @contract DatasetTemplatesState
 *
 * Purpose:
 * - Shape returned by useDatasetTemplates.
 *
 * Fields:
 * - templates: Live list of dataset templates from Dexie (liveQuery-driven).
 * - selectedTemplate: Single template from Dexie by selectedId (null when not found or no id).
 * - isLoading: True during the initial liveQuery setup.
 * - isLoadingSelected: True during the selected-template liveQuery setup.
 * - isMutating: True when any mutation (create/update/activate/deactivate/delete/sample) is in-flight.
 * - error: Non-null when any operation failed.
 * - create: Create a new draft template from the provided input.
 * - update: Update an existing draft template.
 * - softDelete: Soft-delete a template (marks _deleted; server rejects if active).
 * - activate: Activate a draft template (runs server-side validator; returns issues on 400).
 * - deactivate: Revert an active template back to draft.
 * - sample: Trigger AI sampling on the template source tables.
 * - clearError: Reset the error state.
 *
 * Rules:
 * - Never call mutations inside liveQuery.
 * - isMutating should gate Save / Activate / Delete buttons in the UI.
 *
 * Sync:
 * - templates and selectedTemplate come from Dexie liveQuery (local-first).
 * - Mutations POST to server; Dexie updates after next system sync pull.
 */
export interface DatasetTemplatesState {
  templates: MigrationDatasetTemplate[];
  selectedTemplate: MigrationDatasetTemplate | null;
  isLoading: boolean;
  isLoadingSelected: boolean;
  isMutating: boolean;
  error: string | null;
  create: (input: CreateDatasetTemplateInput) => Promise<MigrationDatasetTemplate | null>;
  update: (id: string, input: UpdateDatasetTemplateInput) => Promise<MigrationDatasetTemplate | null>;
  softDelete: (id: string) => Promise<boolean>;
  activate: (id: string) => Promise<MigrationDatasetTemplate | null>;
  deactivate: (id: string) => Promise<MigrationDatasetTemplate | null>;
  sample: (id: string, input: SampleDatasetTemplateInput) => Promise<MigrationDatasetTemplate | null>;
  clearError: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @hook useDatasetTemplates
 *
 * Purpose:
 * - Provide the dataset template list and a selected-template subscription with CRUD + lifecycle actions.
 *
 * Returns:
 * - See DatasetTemplatesState.
 *
 * Side Effects:
 * - On mount: subscribes to Dexie liveQuery for the template list.
 * - When selectedId changes: subscribes to a second liveQuery for the single template.
 * - Mutations call system-api (online mutations).
 *
 * Offline Behavior:
 * - templates and selectedTemplate from Dexie are available offline.
 * - Mutations (create, update, activate, deactivate, softDelete, sample) require connectivity.
 *
 * @errorHandling
 * Strategy:   All async errors are caught and stored in `error` state.
 * User Impact: DatasetTemplateGrid and DatasetDetailSlideIn show inline error banners.
 * Logging:    REQUIRED — log failures with template id and operation.
 */
export function useDatasetTemplates(
  statusFilter: 'all' | 'draft' | 'active' = 'all',
  selectedId: string | null = null,
): DatasetTemplatesState {
  const [templates, setTemplates] = useState<MigrationDatasetTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<MigrationDatasetTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingSelected, setIsLoadingSelected] = useState(false);
  const [isMutating, setIsMutating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // liveQuery — list (pure read, no writes)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    setIsLoading(true);
    const subscription = liveQuery(() => listDatasetTemplates(statusFilter)).subscribe({
      next: (rows) => {
        setTemplates(rows);
        setIsLoading(false);
      },
      error: (err) => {
        console.error('[useDatasetTemplates] liveQuery (list) error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dataset templates');
        setIsLoading(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [statusFilter]);

  // ---------------------------------------------------------------------------
  // liveQuery — single selected template (pure read, no writes)
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!selectedId) {
      setSelectedTemplate(null);
      setIsLoadingSelected(false);
      return;
    }
    setIsLoadingSelected(true);
    const subscription = liveQuery(() => getDatasetTemplate(selectedId)).subscribe({
      next: (row) => {
        setSelectedTemplate(row);
        setIsLoadingSelected(false);
      },
      error: (err) => {
        console.error('[useDatasetTemplates] liveQuery (selected) error:', err);
        setError(err instanceof Error ? err.message : 'Failed to load dataset template');
        setIsLoadingSelected(false);
      },
    });
    return () => subscription.unsubscribe();
  }, [selectedId]);

  // ---------------------------------------------------------------------------
  // Mutations — called from handlers, never inside liveQuery
  // ---------------------------------------------------------------------------

  const create = useCallback(async (input: CreateDatasetTemplateInput): Promise<MigrationDatasetTemplate | null> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await createDatasetTemplate(input);
      return result;
    } catch (err) {
      console.error('[useDatasetTemplates] create failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to create dataset template');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const update = useCallback(async (id: string, input: UpdateDatasetTemplateInput): Promise<MigrationDatasetTemplate | null> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await updateDatasetTemplate(id, input);
      return result;
    } catch (err) {
      console.error('[useDatasetTemplates] update failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to update dataset template');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const softDelete = useCallback(async (id: string): Promise<boolean> => {
    setIsMutating(true);
    setError(null);
    try {
      await softDeleteDatasetTemplate(id);
      return true;
    } catch (err) {
      console.error('[useDatasetTemplates] softDelete failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete dataset template');
      return false;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const activate = useCallback(async (id: string): Promise<MigrationDatasetTemplate | null> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await activateDatasetTemplate(id);
      return result;
    } catch (err) {
      console.error('[useDatasetTemplates] activate failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to activate dataset template');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const deactivate = useCallback(async (id: string): Promise<MigrationDatasetTemplate | null> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await deactivateDatasetTemplate(id);
      return result;
    } catch (err) {
      console.error('[useDatasetTemplates] deactivate failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to deactivate dataset template');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const sample = useCallback(async (id: string, input: SampleDatasetTemplateInput): Promise<MigrationDatasetTemplate | null> => {
    setIsMutating(true);
    setError(null);
    try {
      const result = await sampleDatasetTemplate(id, input);
      return result;
    } catch (err) {
      console.error('[useDatasetTemplates] sample failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to sample dataset template');
      return null;
    } finally {
      setIsMutating(false);
    }
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    templates,
    selectedTemplate,
    isLoading,
    isLoadingSelected,
    isMutating,
    error,
    create,
    update,
    softDelete,
    activate,
    deactivate,
    sample,
    clearError,
  };
}
